/**
 * CapturedItemsPanel Component
 *
 * Displays a summary of all captured items (recordings, streaming transcripts, photos, notes)
 * before submission. Each item type has its own visual style and actions.
 *
 * Supports two variants:
 * - "full" (default): detailed panel with discard/preview actions (used on capture step)
 * - "summary": compact read-only bar showing item counts with upload status (used on project/template steps)
 */

import React from 'react';
import { Mic, Image, StickyNote, FileText, Clock, Volume2, Square, Trash2, Loader2, Languages, Check, AlertCircle, RotateCcw, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui';
import type { UploadStatus } from '@/hooks/useBlobUploader';

export interface AudioRecording {
  id: string;
  blob: Blob;
  duration: number;
}

export interface CapturedStreamingTranscript {
  id: string;
  duration: number;
  transcript?: string;
  isInterrupted?: boolean;
  minutesAgo?: number;
}

interface UploadStatusEntry {
  progress: number;
  status: UploadStatus;
}

interface CapturedItemsPanelProps {
  variant?: 'full' | 'summary';
  recordings: AudioRecording[];
  streamingTranscripts: CapturedStreamingTranscript[];
  photos: Array<{ id: string; timestamp: number }>;
  notes: Array<{ id: string; text: string; timestamp: number }>;
  uploadStatuses?: Map<string, UploadStatusEntry>;
  onPreviewRecording?: (recordingId: string) => void;
  onDiscardRecording?: (recordingId: string) => void;
  onDiscardStreamingTranscript?: (id: string) => void;
  onRetryUpload?: (id: string) => void;
  onBackToCapture?: () => void;
  previewingRecordingId?: string | null;
  previewBlobUrl?: string | null;
  previewLoading?: boolean;
  onStopPreview?: () => void;
  onRemoveNote?: (noteId: string) => void;
  onRemovePhoto?: (photoId: string) => void;
}

/**
 * Format seconds into MM:SS timestamp
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

/**
 * Small upload status indicator shown next to items
 */
const UploadIndicator: React.FC<{ status?: UploadStatusEntry; onRetry?: () => void }> = ({ status, onRetry }) => {
  if (!status) return null;

  if (status.status === 'uploading') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-indigo-600">
        <Loader2 className="w-3 h-3 animate-spin" />
        {status.progress}%
      </span>
    );
  }

  if (status.status === 'uploaded') {
    return (
      <span className="inline-flex items-center text-xs text-emerald-600">
        <Check className="w-3.5 h-3.5" />
      </span>
    );
  }

  if (status.status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1">
        <AlertCircle className="w-3.5 h-3.5 text-red-500" />
        {onRetry && (
          <button
            onClick={(e) => { e.stopPropagation(); onRetry(); }}
            className="text-xs text-red-600 hover:text-red-800 font-medium underline"
          >
            Retry
          </button>
        )}
      </span>
    );
  }

  return null;
};

export const CapturedItemsPanel: React.FC<CapturedItemsPanelProps> = ({
  variant = 'full',
  recordings,
  streamingTranscripts,
  photos,
  notes,
  uploadStatuses,
  onPreviewRecording,
  onDiscardRecording,
  onDiscardStreamingTranscript,
  onRetryUpload,
  onBackToCapture,
  previewingRecordingId,
  previewBlobUrl,
  previewLoading,
  onStopPreview,
  onRemoveNote,
  onRemovePhoto,
}) => {
  const { t } = useTranslation();

  const hasRecordings = recordings.length > 0;
  const hasStreamingTranscripts = streamingTranscripts.length > 0;
  const hasPhotos = photos.length > 0;
  const hasNotes = notes.length > 0;
  const hasAnyContent = hasRecordings || hasStreamingTranscripts || hasPhotos || hasNotes;

  // ──────────────────────────────────────────
  // Summary variant — compact bar for project/template steps
  // ──────────────────────────────────────────
  if (variant === 'summary') {
    if (!hasAnyContent) {
      return (
        <div className="mb-6 bg-slate-50 border border-dashed border-slate-300 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">
              {t('create.noItemsCaptured')}
            </span>
            {onBackToCapture && (
              <button
                onClick={onBackToCapture}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {t('create.backToCapture')}
              </button>
            )}
          </div>
        </div>
      );
    }

    // Compute photo upload summary
    const photoUploadedCount = photos.filter(p => uploadStatuses?.get(p.id)?.status === 'uploaded').length;
    const photoUploadingCount = photos.filter(p => uploadStatuses?.get(p.id)?.status === 'uploading').length;
    const photoFailedCount = photos.filter(p => uploadStatuses?.get(p.id)?.status === 'failed').length;

    return (
      <div className="mb-6 bg-slate-50 border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm font-medium text-slate-700">
              {t('create.capturedItems')}:
            </span>
            {hasRecordings && recordings.map((rec) => (
              <span key={rec.id} className="inline-flex items-center gap-1.5 text-sm text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-lg">
                <Mic className="w-3.5 h-3.5" />
                {formatDuration(rec.duration)}
                <UploadIndicator
                  status={uploadStatuses?.get(rec.id)}
                  onRetry={onRetryUpload ? () => onRetryUpload(rec.id) : undefined}
                />
              </span>
            ))}
            {hasStreamingTranscripts && (
              <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg">
                <Languages className="w-3.5 h-3.5" />
                {streamingTranscripts.length}
              </span>
            )}
            {hasPhotos && (
              <span className="inline-flex items-center gap-1.5 text-sm text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg">
                <Image className="w-3.5 h-3.5" />
                {photos.length}
                {photoUploadingCount > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-xs text-blue-500">
                    <Loader2 className="w-3 h-3 animate-spin" />
                  </span>
                )}
                {photoUploadedCount === photos.length && photos.length > 0 && photoUploadingCount === 0 && photoFailedCount === 0 && (
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                )}
                {photoFailedCount > 0 && (
                  <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                )}
              </span>
            )}
            {hasNotes && (
              <span className="inline-flex items-center gap-1.5 text-sm text-amber-700 bg-amber-50 px-2.5 py-1 rounded-lg">
                <StickyNote className="w-3.5 h-3.5" />
                {notes.length}
              </span>
            )}
          </div>
          {onBackToCapture && (
            <button
              onClick={onBackToCapture}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t('create.backToCapture')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────
  // Full variant — detailed panel with actions
  // ──────────────────────────────────────────
  if (!hasAnyContent) {
    return (
      <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
        <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p className="text-slate-500 font-medium mb-1">{t('create.noItemsCaptured')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5 text-indigo-600" />
        {t('create.capturedItems')}
      </h3>

      <div className="space-y-3">
        {/* Audio Recordings (batch — need transcription) */}
        {hasRecordings && recordings.map((recording) => {
          const isPreviewing = previewingRecordingId === recording.id;
          const isLoadingPreview = isPreviewing && previewLoading;
          const uploadStatus = uploadStatuses?.get(recording.id);

          return (
            <div key={recording.id} className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                    <Mic className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-indigo-900">
                      {t('create.audioRecording')}
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-indigo-600 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDuration(recording.duration)}
                      </p>
                      <UploadIndicator
                        status={uploadStatus}
                        onRetry={onRetryUpload ? () => onRetryUpload(recording.id) : undefined}
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {onPreviewRecording && onStopPreview && (
                    <button
                      onClick={() => isPreviewing ? onStopPreview() : onPreviewRecording(recording.id)}
                      disabled={isLoadingPreview}
                      className="p-2 bg-white text-indigo-700 border border-indigo-300 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors disabled:opacity-50 flex items-center gap-1"
                      aria-label={isPreviewing ? t('create.stopPreview') : t('create.previewRecording')}
                    >
                      {isLoadingPreview ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : isPreviewing ? (
                        <Square className="w-4 h-4" />
                      ) : (
                        <Volume2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  {onDiscardRecording && (
                    <button
                      onClick={() => onDiscardRecording(recording.id)}
                      className="p-2 bg-white text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors flex items-center gap-1"
                      aria-label={t('recorder.discard')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Audio Preview Player */}
              {isPreviewing && previewBlobUrl && (
                <div className="ml-14">
                  <audio
                    controls
                    autoPlay
                    src={previewBlobUrl}
                    className="w-full h-10"
                  />
                </div>
              )}
              {isLoadingPreview && (
                <div className="ml-14 flex items-center gap-2 text-sm text-indigo-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('create.loadingPreview')}
                </div>
              )}
            </div>
          );
        })}

        {/* Streaming Transcripts (already transcribed) */}
        {hasStreamingTranscripts && streamingTranscripts.map((transcript) => (
          <div key={transcript.id} className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                <Languages className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-emerald-900">
                    {t('create.streamingTranscript')}
                  </p>
                  {transcript.isInterrupted && (
                    <Badge variant="warning" size="xs">
                      {t('create.interrupted')}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-emerald-600 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDuration(transcript.duration)}
                  {transcript.isInterrupted && transcript.minutesAgo !== undefined && (
                    <span className="ml-2 text-xs">• {transcript.minutesAgo} min ago</span>
                  )}
                </p>
              </div>
            </div>

            {/* Discard Button */}
            {onDiscardStreamingTranscript && (
              <button
                onClick={() => onDiscardStreamingTranscript(transcript.id)}
                className="p-2 bg-white text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors flex-shrink-0"
                aria-label={t('recorder.discard')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}

        {/* Photos (individual rows with per-photo upload status, retry, and delete) */}
        {hasPhotos && photos.map((photo, index) => {
          const uploadStatus = uploadStatuses?.get(photo.id);

          return (
            <div key={photo.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                  <Image className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-blue-900">
                    {t('create.photo')} {index + 1}
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-blue-600">
                      {new Date(photo.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <UploadIndicator
                      status={uploadStatus}
                      onRetry={onRetryUpload ? () => onRetryUpload(photo.id) : undefined}
                    />
                  </div>
                </div>
              </div>

              {/* Delete Button */}
              {onRemovePhoto && (
                <button
                  onClick={() => onRemovePhoto(photo.id)}
                  className="p-2 bg-white text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors flex-shrink-0"
                  aria-label={t('recorder.discard')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}

        {/* Notes */}
        {hasNotes && notes.map((note) => (
          <div key={note.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                <StickyNote className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-amber-900">{t('create.note')}</p>
                <p className="text-sm text-amber-600 truncate">{note.text}</p>
              </div>
            </div>

            {/* Delete Button */}
            {onRemoveNote && (
              <button
                onClick={() => onRemoveNote(note.id)}
                className="p-2 bg-white text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors flex-shrink-0"
                aria-label={t('recorder.discard')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
